import { useState, type FormEvent, type ReactNode } from 'react';
import { addMemberSchema, addPersonSchema } from '@colette/shared';
import { C, DEGRADE_ACTION } from '../../theme';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import { Champ } from './Champ';
import { Alerte } from './CadreAuth';
import {
  ChampsDateNaissance,
  DATE_VIDE,
  composerDate,
  type DateEnTroisChamps,
} from './ChampsDateNaissance';
import type { RoleMembre } from '../../api/types';

/**
 * Ajouter quelqu'un au foyer.
 *
 * L'API distingue trois cas (membre avec compte, membre sans compte, personne). L'ecran
 * n'en montre que DEUX, parce que la difference entre « membre sans compte » et
 * « personne » n'a aujourd'hui aucune consequence visible : dans les deux cas, Colette
 * connait un nom sans que quiconque se connecte. Exposer les trois ferait poser une
 * question sans reponse utile.
 *
 * Pourquoi cet ecran compte : sans lui, la question « quels enfants ? » ne peut proposer
 * aucun prenom, et Colette ne peut rattacher un evenement a personne.
 */

type Usage = 'utilise' | 'connait';

export function AjouterQuelquun({ onAjoute }: { onAjoute: (message: string) => void }) {
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState('');
  const [naissance, setNaissance] = useState<DateEnTroisChamps>(DATE_VIDE);
  const [usage, setUsage] = useState<Usage>('connait');
  const [role, setRole] = useState<RoleMembre>('CHILD');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const refermer = () => {
    setOuvert(false);
    setNom('');
    setNaissance(DATE_VIDE);
    setUsage('connait');
    setRole('CHILD');
    setEmail('');
    setMotDePasse('');
    setErreur(null);
    // Sans ce rearme, le bouton Ajouter restait grise a la reouverture apres un premier
    // ajout reussi : impossible de declarer un deuxieme enfant (constate en production).
    setEnvoi(false);
  };

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    if (envoi) return;
    setErreur(null);

    // Trois champs Jour / Mois / Annee : rien a interpreter, juste composer.
    // L'existence reelle de la date (31/02, date future) est jugee par birthDateSchema.
    const jourNaissance = composerDate(naissance);
    if (jourNaissance === null) {
      setErreur('Complète la date : le jour, le mois et l’année (4 chiffres) — ou laisse les trois vides');
      return;
    }

    // Les MEMES schemas que l'API : la longueur du mot de passe et la regle
    // « email et mot de passe vont ensemble » ne peuvent pas diverger.
    const verifie =
      usage === 'utilise'
        ? addMemberSchema.safeParse({
            displayName: nom,
            role,
            birthDate: jourNaissance,
            email,
            password: motDePasse,
          })
        : addPersonSchema.safeParse({
            displayName: nom,
            birthDate: jourNaissance,
          });
    if (!verifie.success) {
      setErreur(verifie.error.issues[0]?.message ?? 'Vérifie les informations');
      return;
    }

    setEnvoi(true);
    try {
      if (usage === 'utilise') {
        const donnees = verifie.data as {
          displayName: string;
          role: RoleMembre;
          birthDate?: string;
          email?: string;
          password?: string;
        };
        await api.ajouterMembre(donnees);
      } else {
        await api.ajouterPersonne(verifie.data as { displayName: string; birthDate?: string });
      }
      const ajoute = nom.trim();
      refermer();
      onAjoute(`${ajoute} fait maintenant partie du foyer`);
    } catch (cause) {
      setErreur(messageDErreur(cause));
      setEnvoi(false);
    }
  };

  if (!ouvert) {
    return (
      <button
        type="button"
        onClick={() => setOuvert(true)}
        style={{
          height: 48,
          borderRadius: 14,
          border: `2px dashed ${C.violetBordure}`,
          background: C.carte,
          color: C.violetTexte,
          fontSize: 15,
          fontWeight: 800,
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        + Ajouter quelqu'un au foyer
      </button>
    );
  }

  return (
    <form
      onSubmit={soumettre}
      style={{
        background: C.carte,
        border: `1px solid ${C.violetBordure}`,
        borderRadius: 14,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <Champ libelle="Son prénom" valeur={nom} onChange={setNom} autoFocus />

      <ChampsDateNaissance valeur={naissance} onChange={setNaissance} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.secondaire }}>
          Est-ce que cette personne utilisera Colette ?
        </span>
        <Choix
          actif={usage === 'connait'}
          onClick={() => setUsage('connait')}
          titre="Non, Colette doit juste connaître son prénom"
          detail="Pour rattacher les sorties, les rendez-vous et les listes à la bonne personne."
        />
        <Choix
          actif={usage === 'utilise'}
          onClick={() => setUsage('utilise')}
          titre="Oui, elle aura son propre accès"
          detail="Un compte lui est créé : elle pourra se connecter et voir le foyer."
        />
      </div>

      {usage === 'utilise' ? (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.secondaire }}>Adulte ou enfant ?</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Bascule actif={role === 'ADULT'} onClick={() => setRole('ADULT')}>
                Adulte
              </Bascule>
              <Bascule actif={role === 'CHILD'} onClick={() => setRole('CHILD')}>
                Enfant
              </Bascule>
            </div>
            <span style={{ fontSize: 12.5, color: C.tertiaire, lineHeight: 1.45 }}>
              Seuls les adultes peuvent ajouter quelqu'un au foyer.
            </span>
          </div>

          <Champ
            libelle="Son adresse email"
            type="email"
            inputMode="email"
            autoComplete="off"
            valeur={email}
            onChange={setEmail}
          />
          <Champ
            libelle="Un mot de passe pour elle"
            type="password"
            autoComplete="new-password"
            valeur={motDePasse}
            onChange={setMotDePasse}
            aide="Au moins 10 caractères. Elle pourra le changer une fois connectée."
          />
        </>
      ) : null}

      {erreur !== null ? <Alerte message={erreur} /> : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={refermer}
          style={{
            flex: 1,
            height: 46,
            borderRadius: 12,
            border: `1px solid ${C.bordure}`,
            background: C.carte,
            color: C.secondaire,
            fontSize: 15,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={envoi}
          style={{
            flex: 2,
            height: 46,
            borderRadius: 12,
            border: 'none',
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 15,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: envoi ? 'default' : 'pointer',
          }}
        >
          {envoi ? 'Ajout…' : 'Ajouter'}
        </button>
      </div>
    </form>
  );
}

function Choix({
  actif,
  onClick,
  titre,
  detail,
}: {
  actif: boolean;
  onClick: () => void;
  titre: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={actif}
      onClick={onClick}
      style={{
        textAlign: 'left',
        borderRadius: 12,
        border: `2px solid ${actif ? C.violet : C.bordure}`,
        background: actif ? C.violetFond : C.carte,
        padding: '11px 13px',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontFamily: 'inherit',
      }}
    >
      <span style={{ fontSize: 14.5, fontWeight: 700, color: actif ? C.violetTexte : C.encre }}>
        {titre}
      </span>
      <span style={{ fontSize: 12.5, color: C.secondaire, lineHeight: 1.4 }}>{detail}</span>
    </button>
  );
}

function Bascule({
  actif,
  onClick,
  children,
}: {
  actif: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={actif}
      onClick={onClick}
      style={{
        flex: 1,
        height: 42,
        borderRadius: 11,
        border: `2px solid ${actif ? C.violet : C.bordure}`,
        background: actif ? C.violetFond : C.carte,
        color: actif ? C.violetTexte : C.secondaire,
        fontSize: 14.5,
        fontWeight: 800,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
